import { withRouter, Redirect } from 'react-router-dom'
import React from 'react'
import intl from 'react-intl-universal'
import { connect } from 'react-redux'
import { keepQuery, loadRmaDraft, saveRmaDraft, clearRmaDraft } from '../utils/Tools'
import Popup from 'react-popup'
import sound_not_found from '../assets/not_found_audio.mp3'
import sound_correct_scan from '../assets/correct_scan.mp3'
import { sendData, deselectOrder, deselectOrSearch } from '../actions/RmaActions'
import ProductListItem from './ProductListItem'

const mapStateToProps = (state) => ({
	authenticated: state.auth.session.id !== false,
	group_treshold: state.config.group_treshold,
	order: state.rma.order,
	lang: state.auth.lang,
})

const decisionValues = {
	0: 'valid',
	1: 'invalid',
	2: 'no_refund',
}

const fillDecision = (dict) => {
	let res = {}
	for (const key in dict) {
		if (dict[key] !== undefined) {
			res[key] = decisionValues[dict[key]]
		}
	}
	return res
}

const mapDispatchToProps = (dispatch) => ({
	sendDataAction: (data) => dispatch(sendData(data)),
	deselectOrderAction: () => dispatch(deselectOrder()),
	deselectOrSearchAction: (kod, id) => dispatch(deselectOrSearch(kod, id)),
})

const wrong_ean_audio = new Audio(sound_not_found)
const correct_scan_audio = new Audio(sound_correct_scan)

class OrderComponent extends React.Component {
	constructor(props) {
		super(props)
		const { order } = props
		this.lines = []
		this.savedDraft = loadRmaDraft(order)
		this.scannerInput = React.createRef()
		this.state = {
			valid: false,
			internal_note: order.internal_note || '',
			formCode: '',
		}
	}

	componentDidMount() {
		this.focusScanner()
		this.toggleConfirmButton()
	}

	componentDidUpdate(prevProps) {
		if (prevProps.order !== this.props.order) {
			this.focusScanner()
		}
	}

	focusScanner = () => {
		if (this.scannerInput.current) {
			this.scannerInput.current.focus()
		}
	}

	playSuccessSound = () => {
		correct_scan_audio.currentTime = 0
		correct_scan_audio.play().catch(() => {})
	}

	playErrorSound = () => {
		wrong_ean_audio.currentTime = 0
		wrong_ean_audio.play().catch(() => {})
	}

	getDraftKey = (product, i) => `${i}_${product.id}_${product.sale_order_line_id}`

	getProductListItem = (product, i) => <ProductListItem key={i} childIndex={i} product={product} parent={this} savedDraft={this.savedDraft[this.getDraftKey(product, i)]} />

	saveDraft = () => {
		const { order } = this.props
		const draft = {}
		this.lines.forEach((line, i) => {
			if (line && line.props && line.props.product) {
				draft[this.getDraftKey(line.props.product, i)] = line.getDraft()
			}
		})
		saveRmaDraft(order, draft)
	}

	deselectOrder = (event) => {
		event.preventDefault()
		const { deselectOrSearchAction, order } = this.props
		clearRmaDraft(order)
		this.savedDraft = {}
		const kod = order.code
		const id = null
		deselectOrSearchAction(kod, id)
	}

	noteChange = (event) => {
		this.setState({ internal_note: event.target.value })
	}

	scannerChange = (event) => {
		this.setState({ formCode: event.target.value })
	}

	scannerSubmit = () => {
		const { formCode } = this.state
		const kod = (formCode || '').trim()
		if (!kod) {
			return
		}

		const matchedIndexes = this.findProductIndexesByCode(kod)
		if (matchedIndexes.length === 0) {
			this.playErrorSound()
			Popup.alert(intl.get('Zeskanowany produkt nie występuje w tym zgłoszeniu RMA'))
			this.setState({ formCode: '' }, this.focusScanner)
			return
		}

		let selectedIndex = matchedIndexes.find((index) => !this.isLineFullyValid(index))
		if (typeof selectedIndex === 'undefined') {
			selectedIndex = matchedIndexes[0]
		}

		let targetIndexes = [selectedIndex]
		const relatedBundleIndexes = this.getBundleRelatedIndexes(selectedIndex)
		if (relatedBundleIndexes.length > 0) {
			targetIndexes = Array.from(new Set([selectedIndex, ...relatedBundleIndexes]))
		}

		const results = targetIndexes.map((index) => {
			const matchedLine = this.lines[index]
			if (matchedLine && typeof matchedLine.markAsValidByScan === 'function') {
				return matchedLine.markAsValidByScan()
			}
			return null
		})

		if (targetIndexes.length > 1) {
			if (results.every((result) => result === 'already_valid')) {
				this.playErrorSound()
				if (matchedIndexes.length === 1) {
					Popup.alert(intl.get('Pozycja już oznaczona jako zasadna'))
				}
			} else {
				this.playSuccessSound()
			}
		} else {
			const result = results[0]
			if (result === 'status_changed') {
				this.playSuccessSound()
				Popup.alert(intl.get('Status zmieniony na Zasadna'))
			} else if (result === 'already_valid') {
				this.playErrorSound()
				Popup.alert(intl.get('Pozycja już oznaczona jako zasadna'))
			} else if (result === 'marked_valid') {
				this.playSuccessSound()
			}
		}

		targetIndexes.forEach((index) => this.highlightProductByIndex(index))
		this.setState({ formCode: '' }, this.focusScanner)
	}

	normalizeCode = (value) => (value || '').toString().trim().toLowerCase()

	isLineFullyValid = (index) => {
		const line = this.lines[index]
		if (!line || !line.state || !line.props || !line.props.product) {
			return false
		}

		const qty = line.props.product.quantity % 1 === 0 ? line.props.product.quantity : 1
		for (let i = 0; i < qty; i++) {
			if (line.state.decision[i] !== '0') {
				return false
			}
		}
		return true
	}

	getProductCodes = (product) => {
		const codes = [product.barcode, product.default_code]
		if (Array.isArray(product.additional_barcodes_ids)) {
			product.additional_barcodes_ids.forEach((item) => {
				if (typeof item === 'object' && item !== null) {
					codes.push(item.barcode)
				} else {
					codes.push(item)
				}
			})
		}
		return codes.map(this.normalizeCode).filter(Boolean)
	}

	findProductIndexesByCode = (code) => {
		const { order } = this.props
		if (!order || !Array.isArray(order.lines)) {
			return []
		}
		const normalizedCode = this.normalizeCode(code)
		return order.lines.map((product, index) => (this.getProductCodes(product).includes(normalizedCode) ? index : -1)).filter((index) => index >= 0)
	}

	getBundleRelatedIndexes = (lineIndex) => {
		const { order } = this.props
		if (!order || !Array.isArray(order.lines) || !order.lines[lineIndex]) {
			return []
		}

		const line = order.lines[lineIndex]
		const bundleData = line.recent_bundle_products
		if (!bundleData || typeof bundleData !== 'object') {
			return []
		}

		const bundleIdentifiers = new Set()
		Object.values(bundleData).forEach((item) => {
			if (!item || typeof item !== 'object') {
				return
			}
			const bundleFields = ['barcode', 'default_code', 'code', 'sku', 'name']
			bundleFields.forEach((field) => {
				const normalizedValue = this.normalizeCode(item[field])
				if (normalizedValue) {
					bundleIdentifiers.add(normalizedValue)
				}
			})
		})

		if (!bundleIdentifiers.size) {
			return []
		}

		return order.lines
			.map((product, index) => {
				const candidates = [product.barcode, product.default_code, product.name].map(this.normalizeCode).filter(Boolean)
				return candidates.some((value) => bundleIdentifiers.has(value)) ? index : -1
			})
			.filter((index) => index >= 0)
	}

	highlightProductByIndex = (index) => {
		const node = document.querySelector(`.rma_products .ramka.produkt[data-line-index="${index}"]`)
		if (!node) {
			return
		}
		node.scrollIntoView({ behavior: 'smooth', block: 'center' })
		node.classList.remove('scanned-highlight')
		// Reflow to restart animation when the same product is scanned repeatedly
		void node.offsetWidth
		node.classList.add('scanned-highlight')
		window.setTimeout(() => {
			node.classList.remove('scanned-highlight')
		}, 1400)
	}

	scannerKey = (event) => {
		if (event.which === 13 || event.key === 'Enter') {
			event.preventDefault()
			this.scannerSubmit()
		}
	}

	collectData = () => {
		const { lines } = this
		const { order } = this.props
		const { internal_note } = this.state
		let result = {
			internal_note: internal_note,
			lines: [],
		}
		if (order.id) {
			result.id = order.id
		}
		if (order.order_id) {
			result.order_id = order.order_id
		}
		result.lines = lines.map((line) => {
			const { decision, action } = line.state
			const { product } = line.props
			const decision_filled = fillDecision(decision)
			let items = []
			for (const key in decision_filled) {
				items[key] = {
					decision: decision_filled[key],
					action: action[key],
					corrected_price: line.state.correctedPrices[key] || '',
				}
			}
			return {
				id: product.id,
				sale_order_line_id: product.sale_order_line_id,
				items: items,
			}
		})
		return result
	}

	handleSubmit = (event) => {
		event.preventDefault()
		const { sendDataAction } = this.props
		sendDataAction(this.collectData())
	}

	calculateDecisionCounts = () => {
		const counts = { valid: 0, invalid: 0, no_refund: 0 }
		this.lines.forEach((line) => {
			const { decision } = line.state
			const { product } = line.props
			const qty = product.quantity % 1 === 0 ? product.quantity : 1
			for (let i = 0; i < qty; i++) {
				const decisionName = decisionValues[decision[i]]
				if (decisionName) {
					counts[decisionName] += 1
				}
			}
		})
		return counts
	}

	calculateTotalRefunds = () => {
		const { lines } = this
		let totalRefunds = 0

		lines.forEach((line) => {
			const { decision, action, correctedPrices } = line.state
			const { product } = line.props
			const qty = product.quantity % 1 === 0 ? product.quantity : 1

			for (let i = 0; i < qty; i++) {
				const item_decision = decision[i]
				const item_action = action[i]

				// Check if this is a price refund option (4th element in the option tuple)
				// Sum refunds for both valid (0) and invalid (1) decisions if they have price_refund option
				if ((item_decision === '0' || item_decision === '1') && item_action && product.approve_options) {
					const selectedOption = product.approve_options.find((opt) => opt[0] === parseInt(item_action) && opt[3] === true)

					if (selectedOption) {
						// Use corrected_price if provided, otherwise use product.price_unit
						const corrected_price = correctedPrices[i] || product.price_unit || 0
						// Convert Polish number format (comma as decimal separator) to standard format
						const numericValue = parseFloat(corrected_price.toString().replace(',', '.')) || 0
						if (numericValue > 0) {
							totalRefunds += numericValue
						}
					}
				}
			}
		})

		return totalRefunds
	}

	toggleConfirmButton = () => {
		let state = {}
		const { valid } = this.state
		const { lines } = this
		const validArr = lines.map((line) => {
			const { decision, action } = line.state
			const { product } = line.props
			const qty = product.quantity % 1 === 0 ? product.quantity : 1
			for (let i = 0; i < qty; i++) {
				const itemDecision = decision[i]
				if (typeof itemDecision === 'undefined' || itemDecision === false) {
					return false
				}
				const available_actions =
					{
						0: product.approve_options,
						1: product.decline_options,
						2: [],
					}[itemDecision] || []
				if (available_actions.length > 0) {
					const itemAction = action[i]
					if (typeof itemAction === 'undefined' || itemAction === false) {
						return false
					}
				}
			}
			return true
		})
		this.saveDraft()
		if (validArr.every((v) => v)) {
			state.valid = true
		} else {
			state.valid = false
		}
		if (state.valid !== valid) {
			this.setState(state)
		} else {
			// Force re-render to update total refunds display
			this.setState({})
		}
	}

	render() {
		const { authenticated } = this.props
		if (!authenticated) return <Redirect to={keepQuery('/login')} />
		const { order } = this.props
		const { valid, internal_note, formCode } = this.state
		const totalRefunds = this.calculateTotalRefunds()
		const decisionCounts = this.calculateDecisionCounts()
		return (
			<div className='rma'>
				<div>
					<div className='rma_header'>
						<button className='pull-left secondary' onClick={this.deselectOrder}>
							{intl.get('Anuluj')}
						</button>
						<div className='scanner_wrapper'>
							<input id='scanner' type='text' name='scanner' placeholder={intl.get('Wprowadź kod produktu')} autoComplete='off' onChange={this.scannerChange} onKeyPress={this.scannerKey} value={formCode} ref={this.scannerInput} />
							{order.has_amount_to_refund && (
								<div className='decision_counter'>
									{intl.get('Zasadne')}: {decisionCounts.valid} | {intl.get('Niezasadne')}: {decisionCounts.invalid} | {intl.get('Brak w paczce')}: {decisionCounts.no_refund}
								</div>
							)}
						</div>
						<div className='pull-right' style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<button onClick={this.handleSubmit} id='zatwierdz' disabled={!valid}>
								{intl.get('Dalej')}
							</button>
						</div>
					</div>
					{order && (
						<div className='rma_content row'>
							<div className='col-lg-5'>
								<div className='rma_order ramka'>
									<h4>
										{intl.get('Zamówienie')} {order.order_name}
									</h4>
									<div className='order_data'>
										<div className='date_order f_group'>
											<label>{intl.get('Data zamówienia')}:</label>
											<div>{order.date_order.substring(0, order.date_order.lastIndexOf(':'))}</div>
										</div>
										<div className='amount_total f_group'>
											<label>{intl.get('Numer faktury')}:</label>
											<div>{order.invoice_nr}</div>
										</div>
										<div className='amount_total f_group'>
											<label>{intl.get('Data zafakturowania')}:</label>
											<div>{order.invoice_date}</div>
										</div>
										<div className='amount_total f_group'>
											<label>{intl.get('Sklep')}:</label>
											<div>{order.shop}</div>
										</div>
										<div className='amount_total f_group'>
											<label>{intl.get('Kwota zamówienia')}:</label>
											<div>{order.amount_total}</div>
										</div>
										<div className='amount_total f_group'>
											<label>{intl.get('Forma płatności')}:</label>
											<div>{order.payment_term}</div>
										</div>
										<div className='delivery f_group'>
											<label>{intl.get('Adres dostawy')}:</label>
											<div>
												<div>{order.delivery_name}</div>
												<div>{order.delivery_street}</div>
												<div>
													{order.delivery_zip} {order.delivery_city}
												</div>
											</div>
										</div>
										{order.integration_login && (
											<div className='integration_login f_group'>
												<label>{intl.get('Login integracji')}:</label>
												<div>{order.integration_login}</div>
											</div>
										)}
										{order.integration_id && (
											<div className='integration_login f_group'>
												<label>{intl.get('ID integracji')}:</label>
												<div>{order.integration_id}</div>
											</div>
										)}
										{order.integration_account_name && (
											<div className='integration_login f_group'>
												<label>{intl.get('Konto integracji')}:</label>
												<div>{order.integration_account_name}</div>
											</div>
										)}
										{order.order_url && (
											<div className='go_to_order f_group'>
												<a href={order.order_url} target='_blank' rel='noopener noreferrer'>
													<button className='go_to_order_btn'>{intl.get('Przejdź do zamówienia')}</button>
												</a>
											</div>
										)}
										<div className='notes f_group'>
											<label>{intl.get('Uwagi klienta')}:</label>
											<div>{order.client_note}</div>
										</div>
										<div className='notes f_group'>
											<label>{intl.get('Uwagi własne')}:</label>
											<div>
												<textarea value={internal_note} onChange={this.noteChange} />
											</div>
										</div>
										<div className='total_refunds f_group'>
											<label>{intl.get('Potencjalna suma zwrotów')}:</label>
											<div>
												{totalRefunds.toFixed(2)} {(order && order.currency_symbol) || ''}
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className='rma_products col-lg-7'>
								<div className='rma_legend'>
									<span className='rma_legend__swatch previously-returned' />
									{intl.get('Pozycja wcześniej zwrócona (korekta ilościowa)')}
								</div>
								{order.lines.map((product, i) => this.getProductListItem(product, i))}
							</div>
						</div>
					)}
				</div>
			</div>
		)
	}
}
const Order = connect(mapStateToProps, mapDispatchToProps)(OrderComponent)
export default withRouter(Order)
